import React, { useMemo, useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { Typography } from '@material-ui/core';
import { TIME_AND_EXPENSE_ENTRY_TYPE } from '~/modules/common/enums/TimeAndExpenseEntryType';
import { MORE_AVAILABLE_OPTION_ID } from '~/modules/common/components/SearchAutocomplete/SearchAutocomplete';
import {
  BILLING_STATUS_VALUES,
  BILLING_TYPE,
  NO_TASK_OPTION
} from '../constants';
import {
  createBillingStatusOptionBuilders,
  getBillingOptionsForNonBillable,
  getBillingOptionsForFixedBid,
  getBillingOptionsForTimeAndMaterial,
  getBillingOptionsForBillingContract,
  getEffectiveEntryType
} from '../util';
import { useEnabledBillingRates } from './useEnabledBillingRates';
import { useTaskDetails } from './useTaskDetails';
import { useProjectDetails } from './useProjectDetails';

export const useBillingStatusDropdown = ({
  value,
  onChange,
  sourceProject = null,
  sourceTask = null,
  destinationProject = null,
  destinationTask = null
}) => {
  const { formatMessage } = useIntl();
  const [billingRateSearchTerm, setBillingRateSearchTerm] = useState('');

  const {
    project: sourceProjectDetails,
    loading: sourceProjectDetailsLoading
  } = useProjectDetails({
    projectId: sourceProject?.id,
    skip: !sourceProject?.id
  });

  const {
    project: destinationProjectDetails,
    loading: destinationProjectDetailsLoading
  } = useProjectDetails({
    projectId: destinationProject?.id,
    skip: !destinationProject?.id
  });

  const effectiveSourceProject = sourceProjectDetails || sourceProject;

  const effectiveDestinationProject =
    destinationProjectDetails || destinationProject;

  const hasDestinationProjectAndTask = Boolean(
    destinationProject?.id && destinationTask?.id
  );

  const destinationBillingType = effectiveDestinationProject?.billingType?.uri;

  const {
    task: sourceTaskDetails,
    loading: sourceTaskDetailsLoading
  } = useTaskDetails({
    taskId: sourceTask?.id,
    skip: !sourceTask?.id || sourceTask?.id === NO_TASK_OPTION.id
  });

  const {
    task: destinationTaskDetails,
    loading: destinationTaskDetailsLoading
  } = useTaskDetails({
    taskId: destinationTask?.id,
    skip: !destinationTask?.id || destinationTask?.id === NO_TASK_OPTION.id
  });

  const {
    billingRates = [],
    loading: billingRatesLoading
  } = useEnabledBillingRates({
    projectId: destinationProject?.id,
    textSearch: value?.id ? '' : billingRateSearchTerm,
    skip: !(
      hasDestinationProjectAndTask &&
      destinationBillingType === BILLING_TYPE.TIME_AND_MATERIAL
    )
  });

  const billingStatusOptions = useMemo(() => {
    if (billingRatesLoading) return [];
    const optionBuilders = createBillingStatusOptionBuilders(
      formatMessage,
      billingRates
    );

    if (destinationBillingType === BILLING_TYPE.NON_BILLABLE)
      return getBillingOptionsForNonBillable(optionBuilders);

    const sourceEntryType = getEffectiveEntryType({
      projectEntryTypeUri: effectiveSourceProject?.timeAndExpenseEntryType?.uri,
      taskEntryTypeUri: sourceTaskDetails?.timeAndExpenseEntryType?.uri,
      task: sourceTask
    });

    const effectiveEntryType = getEffectiveEntryType({
      projectEntryTypeUri:
        effectiveDestinationProject?.timeAndExpenseEntryType?.uri,
      taskEntryTypeUri: destinationTaskDetails?.timeAndExpenseEntryType?.uri,
      task: destinationTask
    });

    const includeNoChangeOption =
      sourceProject?.id === destinationProject?.id &&
      sourceEntryType === effectiveEntryType;

    if (destinationBillingType === BILLING_TYPE.FIXED_BID)
      return getBillingOptionsForFixedBid(
        optionBuilders,
        includeNoChangeOption
      );

    if (destinationBillingType === BILLING_TYPE.TIME_AND_MATERIAL)
      return getBillingOptionsForTimeAndMaterial(
        optionBuilders,
        effectiveEntryType,
        includeNoChangeOption
      );

    if (destinationBillingType === BILLING_TYPE.BILLING_CONTRACT)
      return getBillingOptionsForBillingContract(
        optionBuilders,
        effectiveEntryType,
        includeNoChangeOption
      );

    return getBillingOptionsForNonBillable(optionBuilders);
  }, [
    billingRatesLoading,
    formatMessage,
    billingRates,
    destinationBillingType,
    effectiveSourceProject?.timeAndExpenseEntryType?.uri,
    sourceTaskDetails?.timeAndExpenseEntryType?.uri,
    sourceTask,
    effectiveDestinationProject?.timeAndExpenseEntryType?.uri,
    destinationTaskDetails?.timeAndExpenseEntryType?.uri,
    destinationTask,
    sourceProject?.id,
    destinationProject?.id
  ]);

  const handleChange = useCallback(
    (_, val) => {
      onChange(val);
    },
    [onChange]
  );

  const handleInputChange = useCallback((_, newInputValue) => {
    setBillingRateSearchTerm(newInputValue);
  }, []);

  const getOptionLabel = useCallback(
    option => {
      if (!option) return '';

      if (option.id === BILLING_STATUS_VALUES.NO_CHANGE)
        return formatMessage({ id: 'moveTimeDialog.noChangeWithDescription' });

      if (option.isBillingRate)
        return formatMessage(
          { id: 'moveTimeDialog.billableWithRate' },
          { rate: option.displayText }
        );

      return option.displayText || '';
    },
    [formatMessage]
  );

  const getOptionSelected = useCallback(
    (option, selected) => option.id === selected.id,
    []
  );

  const isBillingStatusFieldDisabled =
    !hasDestinationProjectAndTask ||
    sourceProjectDetailsLoading ||
    sourceTaskDetailsLoading ||
    destinationProjectDetailsLoading ||
    destinationTaskDetailsLoading;

  const shouldShowNonBillableLabel = useMemo(() => {
    if (isBillingStatusFieldDisabled) return false;

    if (destinationBillingType === BILLING_TYPE.NON_BILLABLE) return true;

    const effectiveEntryType = getEffectiveEntryType({
      projectEntryTypeUri:
        effectiveDestinationProject?.timeAndExpenseEntryType?.uri,
      taskEntryTypeUri: destinationTaskDetails?.timeAndExpenseEntryType?.uri,
      destinationTask
    });

    if (
      (destinationBillingType === BILLING_TYPE.TIME_AND_MATERIAL ||
        destinationBillingType === BILLING_TYPE.BILLING_CONTRACT) &&
      effectiveEntryType === TIME_AND_EXPENSE_ENTRY_TYPE.NON_BILLABLE
    )
      return true;

    return false;
  }, [
    isBillingStatusFieldDisabled,
    destinationBillingType,
    destinationTaskDetails?.timeAndExpenseEntryType?.uri,
    effectiveDestinationProject?.timeAndExpenseEntryType?.uri,
    destinationTask
  ]);

  const shouldShowBillableLabel = useMemo(() => {
    if (isBillingStatusFieldDisabled) return false;

    if (destinationBillingType === BILLING_TYPE.BILLING_CONTRACT) {
      const effectiveEntryType = getEffectiveEntryType({
        projectEntryTypeUri:
          effectiveDestinationProject?.timeAndExpenseEntryType?.uri,
        taskEntryTypeUri: destinationTaskDetails?.timeAndExpenseEntryType?.uri,
        task: destinationTask
      });

      if (effectiveEntryType === TIME_AND_EXPENSE_ENTRY_TYPE.BILLABLE)
        return true;
    }

    return false;
  }, [
    isBillingStatusFieldDisabled,
    destinationBillingType,
    effectiveDestinationProject?.timeAndExpenseEntryType?.uri,
    destinationTaskDetails?.timeAndExpenseEntryType?.uri,
    destinationTask
  ]);

  const getOptionDisabled = useCallback(
    option => option.id === MORE_AVAILABLE_OPTION_ID,
    []
  );
  const renderOption = useCallback(option => {
    const bodyText = option.isBillingRate
      ? option.description
      : option.displayText;
    const captionText = option.isBillingRate
      ? option.displayText
      : option.description;

    return (
      <div>
        <Typography variant="body1" noWrap>
          {bodyText}
        </Typography>
        {captionText && (
          <Typography variant="caption" color="textSecondary" noWrap>
            {captionText}
          </Typography>
        )}
      </div>
    );
  }, []);

  return {
    billingStatusOptions,
    handleChange,
    handleInputChange,
    getOptionLabel,
    getOptionSelected,
    getOptionDisabled,
    isDisabled: isBillingStatusFieldDisabled,
    shouldShowNonBillableLabel,
    shouldShowBillableLabel,
    loading: billingRatesLoading,
    formatMessage,
    renderOption
  };
};

export default useBillingStatusDropdown;
